import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { readError } from "../../api/client";
import { platformApi } from "../../api/platformClient";
import { ErrorNote } from "../../components/ErrorNote";

/**
 * Every clinic company, and the form that takes on another (ADR-024).
 *
 * Counts, never content. How many clinics and how many staff; not one patient
 * and not one message. The screen says so, because a boundary nobody states is
 * a boundary a clinic has to take on trust.
 */

interface Admin {
  full_name?: string;
  email?: string;
}

interface Organisation {
  invitation_sent?: boolean | null;
  id?: string;
  name?: string;
  clinic_count?: number;
  seat_count?: number;
  admins?: Admin[];
  created_at?: string;
  first_clinic?: { id?: string; name?: string; code?: string } | null;
}

function joined(iso?: string): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-MY", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function Organisations() {
  const [orgs, setOrgs] = useState<Organisation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<Organisation | null>(null);
  const [opening, setOpening] = useState(false);
  const [showing, setShowing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error: failed } = await platformApi.GET("/platform/organizations");
    setLoading(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setOrgs((data?.items ?? []) as Organisation[]);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function takeOn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Both taken before the first await. React clears currentTarget once the
    // handler returns, so reading it afterwards throws, and the throw landed
    // between the reply and the reload: the company was created and the table
    // kept showing the old list.
    const element = event.currentTarget;
    const form = new FormData(element);
    setOpening(true);
    setError(null);
    setMade(null);

    const { data, error: failed } = await platformApi.POST("/platform/organizations", {
      body: {
        name: String(form.get("name") ?? "").trim(),
        admin_full_name: String(form.get("admin_full_name") ?? "").trim(),
        admin_email: String(form.get("admin_email") ?? "").trim(),
        clinic_name: String(form.get("clinic_name") ?? "").trim(),
        clinic_phone: String(form.get("clinic_phone") ?? "").trim(),
      },
    });
    setOpening(false);

    if (failed) {
      setError(readError(failed).message);
      return;
    }
    setMade((data ?? null) as Organisation | null);
    setShowing(false);
    element.reset();
    await load();
  }

  const seats = orgs.reduce((total, org) => total + (org.seat_count ?? 0), 0);
  const clinics = orgs.reduce((total, org) => total + (org.clinic_count ?? 0), 0);

  return (
    <section>
      <div className="plat-head">
        <div>
          <h1 className="screen-head">Clinic companies</h1>
          <p className="screen-lead">
            Each one keeps its own patients, staff and records. Nothing here
            lets one read another, and nothing here reads inside any of them.
          </p>
        </div>
        <button type="button" onClick={() => setShowing((on) => !on)}>
          {showing ? "Leave it" : "Take on a new one"}
        </button>
      </div>

      <ErrorNote message={error} />

      {made && (
        <div className="plat-made" role="status">
          <p>
            <strong>{made.name}</strong> exists.
            {made.first_clinic?.name
              ? ` ${made.first_clinic.name} can take patients now: its poster is already made.`
              : ""}
          </p>
          <p className="muted">
            {made.invitation_sent === false
              ? `We could not email ${made.admins?.[0]?.email}. The company exists; send the invitation again before anybody waits for it.`
              : `An invitation is on its way to ${made.admins?.[0]?.email}. They choose their own password from it, and nobody here ever sees one.`}
          </p>
        </div>
      )}

      {showing && (
        <form className="plat-new" onSubmit={takeOn}>
          <h2>Take on a clinic company</h2>
          <div className="plat-new-fields">
            <label>
              <span>Registered name</span>
              <input name="name" placeholder="Klinik Taman Desa Sdn Bhd" required />
            </label>
            <label>
              <span>Who will run it</span>
              <input name="admin_full_name" placeholder="Dr Tan Mei Ling" required />
            </label>
            <label>
              <span>Their work email</span>
              <input
                name="admin_email"
                type="email"
                placeholder="meiling@tamandesa.example"
                required
              />
            </label>
            <label>
              <span>Their first clinic</span>
              <input name="clinic_name" placeholder="Klinik Taman Desa" required />
            </label>
            <label>
              <span>Its telephone</span>
              <input name="clinic_phone" placeholder="+60 3-7980 2211" />
            </label>
          </div>
          <p className="plat-caution">
            From the moment this exists you cannot see inside it: not a patient,
            not a message, not a record. Its admin works at the clinic you name
            here, and opens any others themselves.
          </p>
          <button type="submit" disabled={opening}>
            {opening ? "Creating…" : "Create and hand over"}
          </button>
        </form>
      )}

      <div className="plat-tiles">
        <div>
          <p className="plat-tile-label">Companies</p>
          <p className="plat-tile-value">{orgs.length}</p>
        </div>
        <div>
          <p className="plat-tile-label">Clinics</p>
          <p className="plat-tile-value">{clinics}</p>
        </div>
        <div>
          <p className="plat-tile-label">Seats in use</p>
          <p className="plat-tile-value">{seats}</p>
          <p className="plat-tile-note">What the invoice is built from</p>
        </div>
      </div>

      {loading ? (
        <p className="empty">Reading the platform…</p>
      ) : orgs.length === 0 ? (
        <p className="empty">No clinic companies yet. The form above makes the first.</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Clinics</th>
                <th>Seats</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => (
                <tr key={org.id}>
                  <td>
                    <Link to={`organisations/${org.id}`}>{org.name}</Link>
                    <p className="muted">
                      {org.admins?.length
                        ? org.admins.map((a) => a.email).join(", ")
                        : "Nobody runs it yet"}
                    </p>
                  </td>
                  <td>{org.clinic_count ?? 0}</td>
                  <td>{org.seat_count ?? 0}</td>
                  <td>{joined(org.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
